import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Input,
  InputGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { injectIntl } from "react-intl";
import axios from "axios";
import { END_POINT } from "../../constants/defaultValues";
import { NotificationManager } from "../../components/common/react-notifications";
import { AddCommaNumber } from "../../helpers/Utils";
import {
  calculateOrderAdjustmentAmount,
  getFeeApplicationBase,
  parseOrderAdjustmentValue,
} from "../../helpers/OrderAdjustmentCalculations";
import { getFeeOtherLabel } from "../../helpers/FeeOtherLabel";
import "../../assets/css/order-adjustment-modals.css";

const ADJUSTMENT_TYPES = {
  DISCOUNT: "discount",
  TAX: "tax",
  FEE: "fee",
};

const emptyDraft = {
  value: "",
  type: "percent",
  note: "",
  label: "shipping",
  calculation: "added",
  applyOn: "after_discount",
};

const formatMoney = (value) => `${AddCommaNumber(Math.round(value || 0))}đ`;

export const OrderAdjustmentModals = ({
  activeType = ADJUSTMENT_TYPES.DISCOUNT,
  customerPhone = "",
  intl,
  isOpen,
  onSubmit,
  onToggle,
  orders = [],
  selectedPromotionsFromParent = [],
  values_discount,
  values_fee,
  values_tax,
}) => {
  const { messages } = intl;
  const valueInputRef = useRef(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [selectedPromotions, setSelectedPromotions] = useState([]);

  const msg = (key, fallback) => messages[key] || fallback;

  useEffect(() => {
    if (!isOpen) return;

    const source =
      activeType === ADJUSTMENT_TYPES.TAX
        ? values_tax
        : activeType === ADJUSTMENT_TYPES.FEE
          ? values_fee
          : values_discount;

    setDraft({
      ...emptyDraft,
      ...(source || {}),
      label: (source && source.label) || "shipping",
    });
    setSelectedPromotions(selectedPromotionsFromParent || []);
    setCouponCode("");
  }, [
    activeType,
    isOpen,
    selectedPromotionsFromParent,
    values_discount,
    values_fee,
    values_tax,
  ]);

  const subtotal = orders.reduce(
    (sum, item) =>
      sum + (parseFloat(item.price) || 0) * (parseFloat(item.count) || 1),
    0,
  );
  const savedDiscount = values_discount && values_discount.saved
    ? calculateOrderAdjustmentAmount(subtotal, values_discount)
    : 0;
  const afterDiscount = Math.max(0, subtotal - savedDiscount);
  const savedTax = values_tax && values_tax.saved
    ? calculateOrderAdjustmentAmount(afterDiscount, values_tax)
    : 0;
  const savedTaxPayable =
    values_tax && values_tax.calculation === "included" ? 0 : savedTax;

  const promotionDiscount = selectedPromotions.reduce(
    (sum, promotion) =>
      sum +
      parseOrderAdjustmentValue(promotion && promotion.benefit?.actualDiscount),
    0,
  );
  const draftBase =
    activeType === ADJUSTMENT_TYPES.FEE
      ? getFeeApplicationBase(subtotal, afterDiscount, draft.applyOn)
      : subtotal;
  const manualAmount = calculateOrderAdjustmentAmount(draftBase, draft);
  const previewAmount =
    activeType === ADJUSTMENT_TYPES.DISCOUNT && promotionDiscount > 0
      ? Math.min(subtotal, promotionDiscount)
      : activeType === ADJUSTMENT_TYPES.DISCOUNT
        ? Math.min(subtotal, manualAmount)
        : manualAmount;

  const updateDraft = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const updateValue = (value) => {
    const normalizedValue =
      value === "" || value === null || value === undefined
        ? ""
        : String(value).replace(/\D/g, "");
    updateDraft("value", normalizedValue);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    if (!orders.length) {
      NotificationManager.warning(
        msg(
          "order.adjustment.add-products-first",
          "Vui lòng thêm sản phẩm trước khi nhập mã",
        ),
        "",
        3000,
      );
      return;
    }

    setCouponLoading(true);
    try {
      const tokenStr = localStorage.getItem("access_token");
      const cartData = orders.map((item) => ({
        id: item.id,
        quantity: item.count || 1,
        price: parseFloat(item.price) || 0,
        category_id: item.category ? item.category.id : null,
      }));
      const response = await axios.post(
        `${END_POINT}/promotion/validate-code`,
        {
          code: couponCode.trim(),
          cartData,
          customerPhone: customerPhone || null,
        },
        { headers: { Authorization: `Bearer ${tokenStr}` } },
      );
      const promotion = response.data && response.data.data;

      if (promotion) {
        setSelectedPromotions((current) => {
          const withoutSameType = promotion.isStackable
            ? current
            : current.filter(
                (item) => item.promotionType !== promotion.promotionType,
              );
          return withoutSameType.some((item) => item.id === promotion.id)
            ? withoutSameType
            : [...withoutSameType, promotion];
        });
        NotificationManager.success(response.data.responseText, "", 3000);
        setCouponCode("");
      }
    } catch (error) {
      NotificationManager.error(
        error.response?.data?.responseText ||
          msg("order.adjustment.invalid-code", "Mã khuyến mại không hợp lệ"),
        "",
        3000,
      );
    } finally {
      setCouponLoading(false);
    }
  };

  const handleApply = () => {
    onSubmit(activeType, draft, selectedPromotions);
  };

  const renderValueControl = (label) => (
    <div className="order-adjustment-field">
      <Label>{label}</Label>
      <InputGroup className="order-adjustment-value">
        <Input
          innerRef={valueInputRef}
          name="value"
          onChange={(event) => updateValue(event.target.value)}
          placeholder="0"
          type="text"
          value={AddCommaNumber(draft.value)}
        />
        <Button
          active={draft.type === "percent"}
          color="primary"
          onClick={() => updateDraft("type", "percent")}
          outline
          type="button"
        >
          %
        </Button>
        <Button
          active={draft.type === "price"}
          color="primary"
          onClick={() => updateDraft("type", "price")}
          outline
          type="button"
        >
          VNĐ
        </Button>
      </InputGroup>
    </div>
  );

  const renderEstimate = (label) => (
    <div className="order-adjustment-estimate">
      <i className="simple-icon-check" />
      <span>
        {label}: <strong>{formatMoney(previewAmount)}</strong>
      </span>
    </div>
  );

  const renderNote = (placeholder) => (
    <div className="order-adjustment-field">
      <Label>
        {msg("order.adjustment.note-optional", "Ghi chú (tùy chọn)")}
      </Label>
      <div className="order-adjustment-note">
        <Input
          maxLength={100}
          name="note"
          onChange={(event) => updateDraft("note", event.target.value)}
          placeholder={placeholder}
          rows={3}
          type="textarea"
          value={draft.note || ""}
        />
        <span>{(draft.note || "").length}/100</span>
      </div>
    </div>
  );

  const renderSummary = (rows, totalLabel, totalValue) => (
    <div className="order-adjustment-summary">
      {rows.map((row) => (
        <div className="order-adjustment-summary-row" key={row.label}>
          <span>
            <i className={row.icon} />
            {row.label}
          </span>
          <strong className={row.highlight ? "text-primary" : ""}>
            {row.value}
          </strong>
        </div>
      ))}
      <div className="order-adjustment-summary-row is-total">
        <span>
          <i className="simple-icon-wallet" />
          {totalLabel}
        </span>
        <strong>{formatMoney(totalValue)}</strong>
      </div>
    </div>
  );

  const renderDiscount = () => (
    <>
      <div className="order-adjustment-field">
        <Label>{msg("order.adjustment.promo-code", "Mã khuyến mại")}</Label>
        <div className="order-adjustment-coupon">
          <div className="order-adjustment-coupon-input">
            <i className="simple-icon-tag" />
            <Input
              disabled={couponLoading}
              onChange={(event) =>
                setCouponCode(event.target.value.toUpperCase())
              }
              onKeyPress={(event) =>
                event.key === "Enter" && handleApplyCoupon()
              }
              placeholder={msg(
                "order.adjustment.promo-placeholder",
                "Nhập mã khuyến mại",
              )}
              value={couponCode}
            />
          </div>
          <Button
            color="primary"
            disabled={couponLoading || !couponCode.trim()}
            onClick={handleApplyCoupon}
          >
            {couponLoading
              ? "..."
              : msg("order.adjustment.apply", "Áp dụng")}
          </Button>
        </div>
        <small className="text-muted">
          {msg(
            "order.adjustment.coupon-help",
            "Nhập mã và nhấn Áp dụng để nhận chiết khấu.",
          )}
        </small>
        {selectedPromotions.length > 0 && (
          <div className="order-adjustment-applied-promotions">
            {selectedPromotions.map((promotion) => (
              <span key={promotion.id || promotion.code}>
                <i className="simple-icon-check" />
                {promotion.code || promotion.name}
                <button
                  aria-label={msg(
                    "order.adjustment.remove-code",
                    "Bỏ mã khuyến mại",
                  )}
                  onClick={() =>
                    setSelectedPromotions((current) =>
                      current.filter((item) => item !== promotion),
                    )
                  }
                  type="button"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="order-adjustment-divider" />
      {renderValueControl(
        msg("order.adjustment.discount-value", "Giá trị chiết khấu"),
      )}
      {renderEstimate(
        msg(
          "order.adjustment.estimated-discount",
          "Số tiền giảm dự kiến",
        ),
      )}
      {renderNote(
        msg(
          "order.adjustment.discount-note-placeholder",
          "Ví dụ: Khách hàng thân thiết",
        ),
      )}
      {renderSummary(
        [
          {
            icon: "simple-icon-doc",
            label: msg("order.adjustment.subtotal", "Tạm tính"),
            value: formatMoney(subtotal),
          },
          {
            highlight: true,
            icon: "simple-icon-tag",
            label: msg("discount.discount", "Chiết khấu"),
            value: `-${formatMoney(previewAmount)}`,
          },
        ],
        msg(
          "order.adjustment.total-after-discount",
          "Tổng sau chiết khấu",
        ),
        Math.max(0, subtotal - previewAmount),
      )}
    </>
  );

  const renderTax = () => {
    const taxBase = afterDiscount;
    const taxPreview = calculateOrderAdjustmentAmount(taxBase, draft);
    const total =
      taxBase + (draft.calculation === "included" ? 0 : taxPreview);

    return (
      <>
        <div className="order-adjustment-field">
          <Label>{msg("order.adjustment.tax-rate", "Thuế suất")}</Label>
          <div className="order-adjustment-presets">
            {[0, 5, 8, 10].map((rate) => (
              <Button
                active={
                  draft.type === "percent" &&
                  draft.value !== "" &&
                  parseOrderAdjustmentValue(draft.value) === rate
                }
                color="primary"
                key={rate}
                onClick={() => {
                  updateDraft("type", "percent");
                  updateValue(rate);
                }}
                outline
              >
                {rate}%
              </Button>
            ))}
            <Button
              active={
                draft.type === "percent" &&
                ![0, 5, 8, 10].includes(
                  parseOrderAdjustmentValue(draft.value),
                )
              }
              color="primary"
              onClick={() => {
                updateDraft("type", "percent");
                updateValue("");
                valueInputRef.current && valueInputRef.current.focus();
              }}
              outline
            >
              {msg("order.adjustment.other", "Khác")}
            </Button>
          </div>
        </div>
        {renderValueControl(
          msg("order.adjustment.tax-value", "Giá trị thuế"),
        )}
        <div className="order-adjustment-estimate">
          <i className="simple-icon-check" />
          <span>
            {msg("order.adjustment.estimated-tax", "Tiền thuế dự kiến")}:{" "}
            <strong>{formatMoney(taxPreview)}</strong>
          </span>
        </div>
        <div className="order-adjustment-field">
          <Label>{msg("order.adjustment.calculation", "Cách tính")}</Label>
          <div className="order-adjustment-calculation">
            <label>
              <Input
                checked={draft.calculation !== "included"}
                name="tax-calculation"
                onChange={() => updateDraft("calculation", "added")}
                type="radio"
                value="added"
              />
              <span>
                <strong>
                  {msg(
                    "order.adjustment.tax-added",
                    "Cộng thêm vào đơn hàng",
                  )}
                </strong>
                <small>
                  {msg(
                    "order.adjustment.tax-added-help",
                    "Thuế sẽ được cộng thêm vào tổng tiền thanh toán.",
                  )}
                </small>
              </span>
            </label>
            <label>
              <Input
                checked={draft.calculation === "included"}
                name="tax-calculation"
                onChange={() => updateDraft("calculation", "included")}
                type="radio"
                value="included"
              />
              <span>
                <strong>
                  {msg(
                    "order.adjustment.tax-included",
                    "Đã bao gồm trong giá",
                  )}
                </strong>
                <small>
                  {msg(
                    "order.adjustment.tax-included-help",
                    "Thuế đã được tính trong giá sản phẩm/dịch vụ.",
                  )}
                </small>
              </span>
            </label>
          </div>
        </div>
        {renderNote(
          msg(
            "order.adjustment.tax-note-placeholder",
            "Ví dụ: Thuế VAT cho đơn bán lẻ",
          ),
        )}
        {renderSummary(
          [
            {
              icon: "simple-icon-doc",
              label: msg("order.adjustment.subtotal", "Tạm tính"),
              value: formatMoney(taxBase),
            },
            {
              icon: "simple-icon-tag",
              label: `${msg("discount.tax", "Thuế")} (${draft.type === "percent" ? `${parseOrderAdjustmentValue(draft.value)}%` : "VNĐ"})`,
              value: formatMoney(taxPreview),
            },
          ],
          msg("order.adjustment.total-payment", "Tổng thanh toán"),
          total,
        )}
      </>
    );
  };

  const renderFee = () => {
    const isPercentageFee = draft.type === "percent";
    const feeBase = getFeeApplicationBase(
      subtotal,
      afterDiscount,
      draft.applyOn,
    );
    const feePreview = calculateOrderAdjustmentAmount(feeBase, draft);
    const total = afterDiscount + savedTaxPayable + feePreview;

    return (
      <>
        <div className="order-adjustment-field">
          <Label>{msg("order.adjustment.fee-name", "Tên phụ phí")}</Label>
          <div className="order-adjustment-select">
            <i className="simple-icon-tag" />
            <Input
              onChange={(event) => updateDraft("label", event.target.value)}
              type="select"
              value={draft.label || "shipping"}
            >
              <option value="shipping">
                {msg("order.adjustment.shipping-fee", "Phí vận chuyển")}
              </option>
              <option value="service">
                {msg("order.adjustment.service-fee", "Phí dịch vụ")}
              </option>
              <option value="packing">
                {msg("order.adjustment.packing-fee", "Phí đóng gói")}
              </option>
              <option value="other">
                {msg("order.adjustment.other-fee", "Phí khác")}
              </option>
            </Input>
          </div>
        </div>
        {renderValueControl(
          msg("order.adjustment.fee-value", "Giá trị phụ phí"),
        )}
        <div className="order-adjustment-estimate">
          <i className="simple-icon-check" />
          <span>
            {msg("order.adjustment.estimated-fee", "Phụ phí dự kiến")}:{" "}
            <strong>{formatMoney(feePreview)}</strong>
            {isPercentageFee && (
              <small className="d-block text-muted">
                {parseOrderAdjustmentValue(draft.value)}% ×{" "}
                {formatMoney(feeBase)}
              </small>
            )}
          </span>
        </div>
        <div className="order-adjustment-field">
          <Label>{msg("order.adjustment.apply-on", "Áp dụng trên")}</Label>
          <div className="order-adjustment-select">
            <i className="simple-icon-target" />
            <Input
              disabled={!isPercentageFee}
              name="fee-apply-on"
              onChange={(event) => updateDraft("applyOn", event.target.value)}
              type="select"
              value={draft.applyOn || "after_discount"}
            >
              <option value="after_discount">
                {msg(
                  "order.adjustment.after-discount",
                  "Tổng tiền sau chiết khấu",
                )}
              </option>
              <option value="subtotal">
                {msg("order.adjustment.subtotal", "Tạm tính")}
              </option>
            </Input>
          </div>
          <small className="d-block mt-1 text-muted">
            {isPercentageFee
              ? `${msg("order.adjustment.calculation-base", "Cơ sở tính phụ phí")}: ${formatMoney(feeBase)}`
              : msg(
                  "order.adjustment.fixed-fee-base-help",
                  "Phụ phí cố định theo VNĐ không phụ thuộc cơ sở tính.",
                )}
          </small>
        </div>
        {renderNote(
          msg(
            "order.adjustment.fee-note-placeholder",
            "Ví dụ: Giao hàng nội thành",
          ),
        )}
        {renderSummary(
          [
            {
              icon: "simple-icon-doc",
              label: msg("order.adjustment.subtotal", "Tạm tính"),
              value: formatMoney(afterDiscount + savedTaxPayable),
            },
            {
              highlight: true,
              icon: "simple-icon-tag",
              label: getFeeOtherLabel(messages, draft),
              value: formatMoney(feePreview),
            },
          ],
          msg("order.adjustment.total-payment", "Tổng thanh toán"),
          total,
        )}
      </>
    );
  };

  const title =
    activeType === ADJUSTMENT_TYPES.TAX
      ? msg("order.adjustment.tax-title", "Thêm thuế")
      : activeType === ADJUSTMENT_TYPES.FEE
        ? msg("order.adjustment.fee-title", "Thêm phụ phí")
        : msg("discount.add-new-modal-title", "Thêm chiết khấu");

  return (
    <Modal
      autoFocus={false}
      backdrop
      className="order-adjustment-dialog"
      isOpen={isOpen}
      toggle={onToggle}
      wrapClassName="modal-right order-adjustment-modal"
    >
      <ModalHeader toggle={onToggle}>{title}</ModalHeader>
      <ModalBody data-adjustment-modal={activeType}>
        {activeType === ADJUSTMENT_TYPES.TAX
          ? renderTax()
          : activeType === ADJUSTMENT_TYPES.FEE
            ? renderFee()
            : renderDiscount()}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={onToggle} outline>
          {msg("general.btn-cancel", "Hủy")}
        </Button>
        <Button color="primary" onClick={handleApply}>
          {msg("order.adjustment.apply", "Áp dụng")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export {
  ADJUSTMENT_TYPES,
  calculateOrderAdjustmentAmount as calculateAdjustment,
};
export default injectIntl(OrderAdjustmentModals);
